import React from 'react';
import { connect } from 'react-redux';
import { reset } from 'redux-form';
import _get from 'lodash/get';
import { hooks, constants } from '@lifesize/clients.sdk';
import {
  callActions,
  contactSelectors,
  currentUserSelectors,
  environmentSelectors,
  featureSelectors,
  pstnNumbersSelectors,
  searchSelectors
} from '@lifesize/nucleus';
import actions from 'actions/AddParticipantDrawer';
import { getInviteForExtension, getInviteMailLinkForExtension } from 'actions/Invite';
import AddParticipantDrawer from 'components/AddParticipantDrawer/AddParticipantDrawer';
import { ADD_PARTICIPANT_DRAWER, ADD_PARTICIPANT_FORM } from 'constants/AddParticipantDrawer';
import { FLAG_WAITING_ROOM } from 'constants/FeatureFlags';
import { filterSearchedContactsInCall } from 'utils/contact-utils';

const otherWaysURL = process.env.REACT_APP_OTHER_WAYS_URL;

const mapStateToProps = (state, ownProps) => {
  const { invited, isUserModerator, isWaitingRoomEnabled, participants } = ownProps;
  const invitee = invited;
  const isWaitingRoomEnabledByModerator = isWaitingRoomEnabled && !isUserModerator;
  const calling = !!invitee;
  const currentUser = currentUserSelectors.myInfo(state);
  const conferenceUuid = _get(ownProps, 'conference.uuid');

  const meetingInfo = conferenceUuid ? contactSelectors.getContactByUuidGenerator(state)(conferenceUuid) : currentUser;

  const searchString = _get(state, 'app.form.participantSearch.values.addParticipantSearchBar');
  const searchResults = filterSearchedContactsInCall(
    searchSelectors.searchUsersAndRoomSystemsOutsideCall(state),
    participants
  );
  const manualDial = !searchResults.length && !!searchString;
  const callingError = invitee?.state === 'failed';
  const callType = callingError ? 'unreachable' : 'calling';

  let participantAdded = false;
  const invitationContact = contactSelectors.getContactByDialStringGenerator(state)(_get(invitee, 'dialString'));
  const contact = _get(state, 'app.addParticipantDrawer.contact');
  const isDesktop = environmentSelectors.platform(state).isDesktop;
  const isVisible = _get(state, 'app.inCallDrawer.subComponentType') === ADD_PARTICIPANT_DRAWER;
  const additionalNumbersURL = _get(currentUserSelectors.myInfo(state), 'additionalNumbersURL');
  const pstnNumbers = pstnNumbersSelectors.pstnNumbersWithCountryName(state);
  const isFocused = _get(state, 'app.addParticipantDrawer.isFocused');
  const waitingRoomFeatureEnabled = !!featureSelectors.getFeatureFlag(state)(FLAG_WAITING_ROOM);
  const isVMR = _get(ownProps, 'conference.type') === 'VMR';

  const localGroupUuid = currentUserSelectors.myInfo(state).groupId;
  // Default isCrossGroup to true, otherwise, UI state will change after all async info is loaded
  const isCrossGroup = isVMR && conferenceUuid && localGroupUuid !== meetingInfo?.groupUUID;
  // Allow invitation from directory if moderator, owner, or superUser+ (or if the meeting is not 'locked'/'waiting room enabled')
  const canInviteFromDirectory =
    isUserModerator ||
    !!currentUser.isSuperUser ||
    _get(ownProps, 'conference.ownerUuid') === currentUser.uuid ||
    !isWaitingRoomEnabled;

  return {
    additionalNumbersURL,
    callType,
    calling,
    callingError,
    canAddParticipant: true,
    canInviteFromDirectory,
    contact,
    invitationContact,
    invitee,
    isCrossGroup,
    isDesktop,
    isFocused,
    isGalaxyCall: true,
    isWaitingRoomEnabledByModerator,
    isVisible,
    manualDial,
    isUserModerator,
    meetingInfo: meetingInfo || currentUser,
    otherWaysToJoinURL: `${otherWaysURL}otherways/${(meetingInfo || currentUser)?.dialString}`,
    participantAdded,
    pstnNumbers,
    searchString,
    waitingRoomFeatureEnabled
  };
};

const mapDispatchToProps = (dispatch) => ({
  dialExtension: (extension) => dispatch(callActions.call(extension)),
  endOutgoingCall: (contact) => {
    dispatch(callActions.cancel(contact));
  },
  clearOutgoingCall: (invitee) => {
    if (invitee) {
      dispatch(callActions.cancel(invitee));
    }
  },
  call: (contact) => {
    if (contact) {
      dispatch(callActions.call(contact));
    }
  },
  getInviteMailLinkForExtension: async (extension) => dispatch(getInviteMailLinkForExtension(extension)),
  getInviteForExtension: async (extension) => dispatch(getInviteForExtension(extension)),
  clearSelectedCall: () => {
    dispatch(reset(ADD_PARTICIPANT_FORM));
    dispatch(actions.clearAddList());
    dispatch(actions.setFormFocus(false));
  }
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const extension = _get(stateProps, 'meetingInfo.dialString');
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    getInviteForExtension: async () => dispatchProps.getInviteForExtension(extension),
    getInviteMailLinkForExtension: async () => dispatchProps.getInviteMailLinkForExtension(extension)
  };
};

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps, mergeProps)(AddParticipantDrawer);

const FunctionalComponent = (props) => {
  const callState = hooks.useCallState();
  const conference = hooks.useConferenceInfo();
  const invitee = hooks.useInvited();
  const isUserModerator = hooks.useIsCurrentUserModerator();
  const isWaitingRoomEnabled = hooks.useIsWaitingRoomEnabled();
  const participants = hooks.useParticipants();
  const invited = invitee.state !== constants.GALAXY_INVITEE_STATE_CONNECTED && _get(invitee, 'invitedId') && invitee;

  return (
    <ConnectedComponent
      {...props}
      callState={callState}
      conference={conference}
      invited={invited}
      isUserModerator={isUserModerator}
      isWaitingRoomEnabled={isWaitingRoomEnabled}
      participants={participants}
    />
  );
};

export { ConnectedComponent as AddParticipantDrawerContainer };

export default FunctionalComponent;
